import { POSITIONS } from ".";
import { IS_SAFARI, IS_WEB } from "../config";
import {
  infoEl,
  infoTextEl,
  infoArrowLeftEl,
  infoArrowRightEl,
  infoBgEl,
  infoArrowBottomEl,
  infoArrowTopEl,
  infoCloseBtnEl,
} from "../elements";
import { addTouchEvent } from "../events";
import { resetToggleTipTimer } from "../game/helper";
import { getLogger } from "../logging";
import { playMp3 } from "../sound";
import store from "../store";
import { autoTranslateElements, resetLangAttributeOfLeafs } from "../translate";

const log = getLogger("Info/Modal");

const _TIMEOUT = 3000;
const _OFFSET = 8;
let timer = null;

let typingActive = false;

function _typeWriter({
  el,
  containerEl,
  speed = 60,
  text = null,
  delay = 100,
}) {
  if (typingActive) {
    log.warn("Typing is already active");
    return;
  }
  typingActive = true;
  text = text || el.innerHTML;

  containerEl.style.minWidth = null;

  const tempEl = containerEl.cloneNode(true);
  tempEl.firstChild.innerHTML = text;
  tempEl.style.visibility = "hidden";
  tempEl.style.padding = 0;
  document.body.appendChild(tempEl);
  const minWidth = tempEl.clientWidth + "px";
  document.body.removeChild(tempEl);

  let i = 0;
  let j = 0;
  containerEl.style.minWidth = minWidth;
  el.innerHTML = "";
  function typing() {
    const characters = Array.from(text);
    if (i < characters.length && containerEl.style.display != "none") {
      const newCharacter = characters[i++];
      el.innerHTML += newCharacter;
      const includesPauseCharacter = [
        " ",
        ".",
        ",",
        ";",
        "!",
        "?",
        "–",
      ].includes(newCharacter);
      if (includesPauseCharacter) j = 0;
      else if (j++ % 4 == 0 && !(IS_SAFARI && IS_WEB)) playMp3("type");
      setTimeout(typing, includesPauseCharacter ? speed * 3 : speed / 3);
    } else {
      containerEl.style.width = null;
      typingActive = false;
    }
  }
  if (delay) setTimeout(typing, delay);
  else typing();
}

export function showInfo({
  el = null,
  position,
  text,
  innerHTML = null,
  persist = false,
  closeBtn = false,
  typeWriter = false,
  delay = 100,
  skipTranslate = false,
} = {}) {
  // write ops
  infoArrowLeftEl.style.display = "none";
  infoArrowRightEl.style.display = "none";
  infoArrowBottomEl.style.display = "none";
  infoArrowTopEl.style.display = "none";
  if (innerHTML) infoTextEl.innerHTML = innerHTML;
  else infoTextEl.textContent = text;
  infoEl.style.display = "initial";
  if (skipTranslate) infoEl.classList.add("notranslate");
  else if (infoEl.classList.contains("notranslate"))
    infoEl.classList.remove("notranslate");
  resetLangAttributeOfLeafs(infoTextEl);

  // read ops
  const elBbox = el
    ? el.getBoundingClientRect()
    : {
        left: store.innerWidth / 2,
        top: store.innerHeight / 2,
        right: store.innerWidth / 2,
        bottom: store.innerHeight / 2,
        width: 0,
        height: 0,
      };
  const infoBbox = infoEl.getBoundingClientRect();

  // read ops followed by write ops
  switch (position) {
    case POSITIONS.LEFT:
      {
        const infoArrowRightBbox = infoArrowRightEl.getBoundingClientRect();
        infoEl.style.right = `${
          elBbox.right + elBbox.width - infoArrowRightBbox.width - _OFFSET
        }px`;
        infoEl.style.top = `${
          elBbox.top + elBbox.height / 2 - infoBbox.height / 2
        }px`;
        infoArrowRightEl.style.display = "initial";
      }
      break;
    case POSITIONS.RIGHT:
      {
        infoArrowLeftEl.style.display = "initial";
        const infoArrowLeftBbox = infoArrowLeftEl.getBoundingClientRect();
        infoEl.style.left = `${
          elBbox.left + elBbox.width + infoArrowLeftBbox.width + _OFFSET
        }px`;
        infoEl.style.top = `${
          elBbox.top + elBbox.height / 2 - infoBbox.height / 2
        }px`;
      }
      break;
    case POSITIONS.BOTTOM:
      {
        infoArrowTopEl.style.display = "initial";
        const infoArrowTopBox = infoArrowTopEl.getBoundingClientRect();
        infoEl.style.left = `${
          elBbox.left + elBbox.width / 2 - infoBbox.width / 2
        }px`;
        infoEl.style.top = `${
          elBbox.top + elBbox.height + infoArrowTopBox.height + _OFFSET
        }px`;
      }
      break;
    case POSITIONS.TOP:
      {
        infoArrowBottomEl.style.display = "initial";
        const infoArrowBottomBox = infoArrowBottomEl.getBoundingClientRect();
        infoEl.style.left = `${
          elBbox.left + elBbox.width / 2 - infoBbox.width / 2
        }px`;
        infoEl.style.top = `${
          elBbox.top + elBbox.height + infoArrowBottomBox.height + _OFFSET
        }px`;
      }
      break;
    case POSITIONS.NONE:
      infoEl.style.left = `${
        elBbox.left + elBbox.width / 2 - infoBbox.width / 2
      }px`;
      infoEl.style.top = `${elBbox.top + elBbox.height + _OFFSET}px`;
      break;
    case POSITIONS.CENTER:
      infoEl.style.left = `${
        elBbox.left + elBbox.width / 2 - infoBbox.width / 2
      }px`;
      infoEl.style.top = `${
        elBbox.top + elBbox.height - infoBbox.height / 2 + _OFFSET
      }px`;
      break;
    case POSITIONS.COVER:
      infoEl.style.left = `${elBbox.left}px`;
      infoEl.style.right = `${elBbox.right - elBbox.width}px`;
      infoEl.style.top = `${elBbox.top}px`;
      infoEl.style.bottom = `${elBbox.bottom - elBbox.height}px`;
      break;
    default:
      break;
  }
  infoEl.style.visibility = "visible";
  if (timer != null) clearTimeout(timer);
  if (closeBtn) infoCloseBtnEl.style.display = "initial";
  else infoCloseBtnEl.style.display = "none";
  infoBgEl.style.display = "initial";
  if (store.disableTranslate || store.languageCode == "en" || skipTranslate) {
    if (typeWriter)
      _typeWriter({
        el: infoTextEl,
        containerEl: infoEl,
        text,
        delay,
      });
  } else autoTranslateElements(infoTextEl);

  if (persist) return infoEl;
  timer = setTimeout(hideInfo, _TIMEOUT);
  return infoEl;
}

export function hideInfo() {
  infoEl.onHide && infoEl.onHide();
  infoEl.style.display = "none";
  infoEl.style.visibility = "hidden";
  infoBgEl.style.display = "none";
  infoEl.style.display = "none";
  infoEl.style.visibility = "hidden";
  infoEl.style.maxWidth = "50%";
  infoEl.style.left = null;
  infoEl.style.right = null;
  infoEl.style.top = null;
  infoEl.style.bottom = null;
  infoEl.style.minWidth = null;
  infoTextEl.innerHTML = "";
  resetToggleTipTimer();
  resetLangAttributeOfLeafs(infoTextEl);
}

addTouchEvent(infoBgEl, hideInfo);
addTouchEvent(infoCloseBtnEl, hideInfo);
